import clsx from "clsx";
import React, { useEffect, useMemo } from "react";
import { format, isValid } from "date-fns";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import useDeleteEpd from "~/components/Forms/DealerPassportForm2/hook/useDeleteEpd";
import useGetEpd from "~/components/Forms/DealerPassportForm2/hook/useGetEpd";
import useUpdateEpd from "~/components/Forms/DealerPassportForm2/hook/useUpdateEpd";
import DynamicFormField from "~/components/Forms/DealerPassportForm2/UI/DynamicFormField";
import Button from "~/shared/ui/Fields/Button";
import { getDealerEpd } from "~/components/Forms/DealerPassportForm2/Epd/options";
import { NOTIFICATIONS } from "~/constans/notifications";
import { useAddNotificationMutation } from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";

import s from "./Epd.less";

interface EpdProps {
  DealerId: string;
  idArchive?: string;
  allDisabled: boolean;
}

const Epd = ({ DealerId, idArchive, allDisabled }: EpdProps) => {
  const { hasPermission } = usePermissions();
  const { t } = useTranslation();
  const dealerEpd = useMemo(() => getDealerEpd(t), [t]);
  const {
    data: dataDealer,
    isFetching: isLoadingDealer,
    refetch,
  } = useGetEpd(DealerId);
  const { mutate: updateEpd, isLoading: isUpdating } = useUpdateEpd();
  const { mutate: deleteEpd, isLoading: isDeleteUpdating } = useDeleteEpd();
  const [addNotificationMutation] = useAddNotificationMutation({});

  const method = useForm({
    defaultValues: { epdBlocks: [] },
    mode: "onChange",
  });

  useEffect(() => {
    if (dataDealer?.epdBlocks) {
      const epdBlocks = dataDealer.epdBlocks.map((block) => ({
        ...block,
        mchdValidFrom: block.mchdValidFrom
          ? new Date(block.mchdValidFrom)
          : null,
      }));
      method.reset({ epdBlocks });
      method.register("epdBlocks");
    }
  }, [dataDealer]);

  const addEpdBlock = () => {
    const newBlock = {
      id: Date.now(),
      operatorInn: "",
      operatorName: "",
      edoAccountId: "",
      edoOperatorCode: "",
      signerCode: "",
      signerName: "",
      signerPosition: "",
      mchdGuid: "",
      mchdValidFrom: null,
      mchdUrl: "",
      signerAuthorityCodes: "",
      isNew: true,
    };
    const currentBlocks = method.getValues("epdBlocks") || [];

    method.reset({ epdBlocks: [...currentBlocks, newBlock] });
    method.register("epdBlocks");
  };

  const removeEpdBlock = (index) => {
    const epdBlocks = method.getValues("epdBlocks");
    const itemBlock = epdBlocks[index];
    const newBlocks = epdBlocks.filter((item, i) => i !== index);
    if (itemBlock.isNew) {
      method.reset({ epdBlocks: newBlocks });
      method.register("epdBlocks");
    } else {
      deleteEpd(
        { dealerId: DealerId, epdId: itemBlock.id },
        {
          onSuccess: () => {
            method.reset({ epdBlocks: newBlocks });
            method.register("epdBlocks");
          },
          onError: (error) => {
            console.error(`Error deleting epd block ${itemBlock.id}:`, error);
            refetch();
          },
        },
      );
    }
  };

  const onSubmit = (data) => {
    const newData = data?.epdBlocks?.map((item) => {
      const { isNew, ...rest } = item;
      return {
        ...rest,
        id: isNew ? null : Number(item.id),
        mchdValidFrom:
          item.mchdValidFrom && isValid(new Date(item.mchdValidFrom))
            ? format(new Date(item.mchdValidFrom), "yyyy-MM-dd")
            : null,
      };
    });
    updateEpd(
      { dealerId: DealerId, data: newData },
      {
        onSuccess: () => {
          addNotificationMutation({
            variables: {
              type: NOTIFICATIONS.success,
              content: t("common.сhange_was_successful"),
            },
          });
          refetch();
        },
        onError: (error) => {
          console.error("Error updating epd:", error);
        },
      },
    );
  };

  if (isLoadingDealer) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <FormProvider {...method}>
        <form
          onSubmit={method.handleSubmit(onSubmit)}
          className={clsx("dynamic-form")}
        >
          <div className={s.wrapper}>
            {method.getValues("epdBlocks") &&
              method.getValues("epdBlocks").map((item, index) => {
                return (
                  <div className={s.wrap} key={item.id}>
                    <div className={s.wrapperBase}>
                      <div className={s.title}>
                        {t("dealers.epdBlockTitle")} {index + 1}
                        {!allDisabled && hasPermission("dealer.deleteEpd") && (
                          <Button
                            onClick={() => removeEpdBlock(index)}
                            small
                            customClassName={s.butRemove}
                            txtBtn={t("dealers.epdRemoveBlock")}
                          />
                        )}
                      </div>
                      <div>
                        {dealerEpd.map((field) => (
                          <DynamicFormField
                            key={`${item.id}-${field.name}`}
                            {...field}
                            name={`epdBlocks[${index}].${field.name}`}
                            defaultValue={
                              typeof item[field.name] === "object"
                                ? item[field.name]?.value || ""
                                : item[field.name] || ""
                            }
                            disabled={allDisabled || !!idArchive}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            {!allDisabled && !idArchive && (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Button
                  onClick={addEpdBlock}
                  customClassName={s.butEpd}
                  txtBtn={t("dealers.epdAddBlock")}
                />
              </div>
            )}
          </div>
          <div className={s.wrapperBTN}>
            {!allDisabled && !idArchive && (
              <Button
                txtBtn={t("ui.save_changes")}
                orange
                isForm
                customClassName={s.but}
                disabled={isUpdating || isDeleteUpdating}
              />
            )}
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default React.memo(Epd);
